import { memo, ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { usePagination } from './hooks/usePagination';

export interface PaginationProps<T = any> {
  data: T[];
  pageSize: number;
  loadingMore?: boolean;
  getItemKey?: (item: T) => string | number; // Optional: for deduplication
  children: (paginatedData: T[]) => ReactNode;
}

function PaginationComponent<T = any>({
  data,
  pageSize,
  loadingMore = false,
  getItemKey,
  children,
}: PaginationProps<T>) {
  const {
    currentPage,
    totalPages,
    paginatedData,
    startRecord,
    endRecord,
    totalCount,
    handlePrevious,
    handleNext,
  } = usePagination({ data, pageSize, getItemKey });

  const hasPrevious = currentPage > 1;
  const hasNext = currentPage < totalPages;

  return (
    <>
      {/* Render children with paginated data */}
      {children(paginatedData)}

      {/* Pagination controls */}
      {totalCount > 0 && totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-center sm:justify-between gap-3 mt-4">
          <div className="text-sm text-gray-700 text-center sm:text-left flex items-center gap-2">
            <span>
              Showing {startRecord}–{endRecord} of {loadingMore ? `${totalCount}+` : totalCount}&nbsp;{totalCount === 1 ? 'item' : 'items'}
            </span>
            {loadingMore && (
              <svg className="animate-spin h-4 w-4 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
          </div>
          <div className="flex gap-2">
            <button 
              className="inline-flex items-center justify-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white w-[100px]"
              onClick={handlePrevious}
              disabled={!hasPrevious}
              aria-label="Previous page"
            >
              <ChevronLeft size={16} />
              Previous
            </button>
            <button 
              className="inline-flex items-center justify-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white w-[100px]"
              onClick={handleNext}
              disabled={!hasNext}
              aria-label="Next page"
            >
              Next
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export const Pagination = memo(PaginationComponent) as typeof PaginationComponent;
